import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  Fade,
  Grow,
  Collapse,
  Container,
  Stack,
  Link,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { reactLocalStorage } from 'reactjs-localstorage';
import { jwtDecode } from "jwt-decode";
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';

// Microsoft SSO: the API verifies the MSAL ID token server-side
// (POST /api/auth/mstoken), same contract shape as the Google flow.
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";

// Login accent — related to the background purple but distinct from the
// board's LPR indigo, so the badge color stays reserved.
const ACCENT = '#5A4FCF';
const ACCENT_HOVER = '#4C41BD';
const ACCENT_ACTIVE = '#443AA8';
const LOGO_URL = 'https://conciergepad-images.s3.amazonaws.com/concierge_logo.png';

// All three auth calls get a timeout so the signing-in overlay can never
// spin forever on school Wi-Fi.
const AUTH_TIMEOUT_MS = 20000;

// One unified message for every "token worked, profile didn't" failure —
// three different phrasings of the same problem is a support nightmare.
const PROFILE_ERROR =
  "You're signed in, but we couldn't load your school's information. Please wait a moment and try again.";

const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontSize: '16px', // 16px everywhere: easier reading, no iOS zoom
    backgroundColor: '#FAFBFC',
    '& fieldset': { borderColor: '#D5DBE3' },
    '&:hover fieldset': { borderColor: '#A7A2E0' },
    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: '2px' },
  },
  '& .MuiInputLabel-root': { fontSize: '1rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
};

const Login = () => {
  const [email, setEmail] = useState(() => {
    const savedCredentials = localStorage.getItem('carlineCredentials');
    if (savedCredentials) {
      try {
        const { email } = JSON.parse(savedCredentials);
        return email || '';
      } catch (e) {
        return '';
      }
    }
    return '';
  });

  const [password, setPassword] = useState(() => {
    const savedCredentials = localStorage.getItem('carlineCredentials');
    if (savedCredentials) {
      try {
        const { password } = JSON.parse(savedCredentials);
        return password || '';
      } catch (e) {
        return '';
      }
    }
    return '';
  });

  const [rememberMe, setRememberMe] = useState(true); // Default to checked

  const [loading, setLoading] = useState(false);
  const [slowNetwork, setSlowNetwork] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorSeverity, setErrorSeverity] = useState('error');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  // Google leads; the email form starts open only for returning password users
  const [showEmailForm, setShowEmailForm] = useState(() =>
    Boolean(localStorage.getItem('carlineCredentials')) ||
    localStorage.getItem('carlineAuthPref') === 'password'
  );
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const navigate = useNavigate();
  const { instance } = useMsal();

  const saveCredentials = () => {
    if (rememberMe) {
      localStorage.setItem('carlineCredentials', JSON.stringify({
        email: email,
        password: password
      }));
    } else {
      localStorage.removeItem('carlineCredentials');
    }
  };

  // Already signed in? Skip the login screen entirely — tokens last a year,
  // and the board bounces back here automatically if one is ever rejected.
  useEffect(() => {
    const token = localStorage.getItem('jwsToken');
    const profile = localStorage.getItem('userProfile');
    if (token && profile) {
      try {
        const parsed = JSON.parse(profile);
        if (parsed.comp) {
          navigate('/carline', { state: { uuid: parsed.comp, user: parsed.user }, replace: true });
        }
      } catch (e) {
        console.log('Stored profile unreadable:', e);
      }
    }
  }, [navigate]);

  // Update mobile state on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice() || window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Swap the overlay subtext when sign-in is taking a while, so nobody
  // assumes the screen froze and reloads mid-auth.
  useEffect(() => {
    if (!loading) {
      setSlowNetwork(false);
      return;
    }
    const timer = setTimeout(() => setSlowNetwork(true), 8000);
    return () => clearTimeout(timer);
  }, [loading]);

  const validateEmail = (value) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value);
  };

  // Typing only updates state and clears old errors — no scolding after one
  // keystroke. Format is checked on blur and on submit.
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError('');
    setErrorMessage('');
  };

  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setEmailError("That doesn't look like a complete email — check it for typos (e.g. name@yourschool.org)");
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setPasswordError('');
    setErrorMessage('');
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
    // If unchecking, immediately remove saved credentials
    if (!e.target.checked) {
      localStorage.removeItem('carlineCredentials');
    }
  };

  // Single error surface: the persistent inline alert. (The old code showed
  // every error twice — a 5-second toast AND this alert, with identical text.)
  const showAlert = (message, severity = 'error') => {
    setErrorSeverity(severity);
    setErrorMessage(message);
  };

  // The API verifies the Google ID token server-side and extracts the email from
  // it — we send the token; the decoded email is only for display/profile lookups.
  const goToSso = (idToken, userName, profilePic) => {
    console.log("=====================");
    console.log("IN GOOGLE SSO");
    setLoading(true);

    axios.post("https://api.conciergepadedu.com/api/auth/ssotoken", {
      idToken: idToken,
    }, { timeout: AUTH_TIMEOUT_MS }).then(response => {
      // Per the 2026-07-28 contract the 200 body IS the JWT (bare string)
      if (typeof response.data === 'string' && response.data.length > 0) {
        reactLocalStorage.set('logoUrl', profilePic);
        reactLocalStorage.set('profilePic', profilePic);
        getGoogleSsoProfile(response.data, userName);
      } else {
        setLoading(false);
        showAlert("We couldn't finish signing you in. Please try again — if it keeps happening, ask your school's ConciergePad admin for help.");
      }
    }).catch(error => {
      console.log("In the error section");
      console.log(error);
      setLoading(false);

      const status = error.response?.status;
      const body = typeof error.response?.data === 'string'
        ? error.response.data
        : JSON.stringify(error.response?.data || '');

      if (status === 404) {
        // USER_NOT_FOUND: the verified Google email isn't in the staff directory.
        // Warning (not error) — nothing the user did wrong — and it includes the
        // exact email so an admin can add the right account.
        showAlert(`Your Google account (${userName}) isn't in your school's ConciergePad staff list yet. Ask your school's ConciergePad admin to add that exact email, then sign in again.`, 'warning');
      } else if (status === 401 && body.includes('AUD_MISMATCH')) {
        showAlert("Google sign-in isn't fully set up for this app yet — this is a setup issue on our side, not anything you did. Please contact ConciergePad support and mention code AUD_MISMATCH.");
      } else if (status === 401) {
        showAlert('We couldn\'t confirm your Google sign-in. Please press "Continue with Google" and try once more.');
      } else if (error.code === 'ECONNABORTED') {
        showAlert('This is taking longer than usual. Please check the internet connection and try again.');
      } else {
        showAlert("Google sign-in didn't go through. Please try again — if it keeps happening, check your internet connection or ask your admin for help.");
      }
    });
  };

  // Microsoft sign-in: get the MSAL ID token via popup, send ONLY the token
  // to the API (never a bare email as identity), then reuse the exact same
  // profile/session logic as Google.
  const handleMicrosoftLogin = async () => {
    try {
      const loginResponse = await instance.loginPopup(loginRequest);
      if (loginResponse.idToken && loginResponse.account) {
        const userEmail = (loginResponse.account.username || '').toLowerCase();
        const userName = loginResponse.account.name || userEmail;
        const profilePicture = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=0078d4&color=fff`;
        goToMsSso(loginResponse.idToken, userEmail, profilePicture);
      }
    } catch (error) {
      console.error("Microsoft login error:", error.errorCode || error.name);
      // Closing the popup isn't an error worth alarming anyone about
      if (error && (error.errorCode === 'user_cancelled' || error.errorCode === 'popup_window_error')) return;
      showAlert('Microsoft sign-in didn\'t finish — the window may have closed early or pop-ups may be blocked. Please press "Sign in with Microsoft" to try again.');
    }
  };

  const goToMsSso = (idToken, userName, profilePic) => {
    setLoading(true);

    axios.post("https://api.conciergepadedu.com/api/auth/mstoken", {
      idToken: idToken,
    }, { timeout: AUTH_TIMEOUT_MS }).then(response => {
      if (typeof response.data === 'string' && response.data.length > 0) {
        reactLocalStorage.set('logoUrl', profilePic);
        reactLocalStorage.set('profilePic', profilePic);
        getGoogleSsoProfile(response.data, userName);
      } else {
        // Not a staff account — check whether it's a student before giving up
        checkMsStudentAccount(idToken, userName);
      }
    }).catch(error => {
      console.log('MS staff token error status:', error.response?.status);
      const status = error.response?.status;
      if (status === 404) {
        checkMsStudentAccount(idToken, userName);
        return;
      }
      setLoading(false);
      if (status === 401) {
        showAlert('We couldn\'t confirm your Microsoft sign-in. Please press "Sign in with Microsoft" and try once more.');
      } else if (error.code === 'ECONNABORTED') {
        showAlert('This is taking longer than usual. Please check the internet connection and try again.');
      } else {
        showAlert("Microsoft sign-in didn't go through. Please try again — if it keeps happening, check your internet connection or ask your admin for help.");
      }
    });
  };

  // The carline board is a staff tool — a student Microsoft account can't run
  // it. The student endpoint is used only to tell students apart from
  // unknown emails so the message is helpful instead of baffling.
  const checkMsStudentAccount = (idToken, userName) => {
    axios.post("https://api.conciergepadedu.com/api/auth/mssttoken", {
      idToken: idToken,
    }, { timeout: AUTH_TIMEOUT_MS }).then(response => {
      setLoading(false);
      if (typeof response.data === 'string' && response.data.length > 0) {
        showAlert(`${userName} is a student account. The carline board is for staff — please sign in with a staff account.`, 'warning');
      } else {
        showAlert(`No ConciergePad account was found for ${userName}. Please ask your school's ConciergePad admin to add that exact email, then sign in again.`, 'warning');
      }
    }).catch(() => {
      setLoading(false);
      showAlert(`No ConciergePad account was found for ${userName}. Please ask your school's ConciergePad admin to add that exact email, then sign in again.`, 'warning');
    });
  };

  const getGoogleSsoProfile = (accessToken, emailValue) => {
    let bearerConfig = {
      headers: {
        Authorization: "Bearer " + accessToken
      },
      timeout: AUTH_TIMEOUT_MS
    };

    axios.get('https://api.conciergepadedu.com/api/Profiles/GetSso/' + emailValue, bearerConfig).then(response => {
      if (response.data.error) {
        console.log("ERROR");
        console.log('error', response.data.error);
        setLoading(false);
        showAlert(PROFILE_ERROR);
      } else {
        console.log('success', response.data);

        reactLocalStorage.set('jwtToken', accessToken);
        reactLocalStorage.set('userName', emailValue.toLowerCase());
        reactLocalStorage.set('name', response.data[0].empName);
        reactLocalStorage.set('permissionFlag', response.data[0].permissionFlag);
        reactLocalStorage.set('companyName', response.data[0].companyName);
        reactLocalStorage.set('empId', response.data[0].id);
        reactLocalStorage.set('empPhone', response.data[0].empPhone);
        reactLocalStorage.set('endPassFlag', response.data[0].endPassFlag);

        const payload = {
          token: accessToken,
          user: emailValue.toLowerCase(),
          comp: response.data[0].companyName,
          email: emailValue.toLowerCase(),
          pId: response.data[0].id,
          password: password,
        };

        localStorage.setItem('jwsToken', JSON.stringify(accessToken));
        localStorage.setItem('userProfile', JSON.stringify(payload));

        // Go straight to the board — the signing-in overlay covers the
        // transition, no toast-and-wait.
        navigate('/carline', { state: { uuid: response.data[0].companyName, user: emailValue } });
      }
    }).catch(error => {
      setLoading(false);
      showAlert(PROFILE_ERROR);
      console.log("CATCH ERROR");
      console.log(error.response);
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // Field-level validation with focus — never a dead gray button
    if (!email) {
      setEmailError('Please enter your email');
      emailRef.current?.focus();
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("That doesn't look like a complete email — check it for typos (e.g. name@yourschool.org)");
      emailRef.current?.focus();
      return;
    }
    if (!password) {
      setPasswordError('Please enter your password');
      passwordRef.current?.focus();
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('https://api.conciergepadedu.com/api/auth/cptoken', {
        USERNAME: email,
        PASSWORD: password,
      }, { timeout: AUTH_TIMEOUT_MS });

      if (response.data.error) {
        showAlert('That email and password don\'t match our records. Check for typos — passwords are case-sensitive — or use "Forgot your password?" below.');
        setLoading(false);
        return;
      }

      const jwsToken = response.data;

      const bearerConfig = {
        headers: {
          Authorization: `Bearer ${jwsToken}`,
        },
        timeout: AUTH_TIMEOUT_MS,
      };

      const profileResponse = await axios.get(`https://api.conciergepadedu.com/api/Profiles/${email}`, bearerConfig);

      if (profileResponse.data.error) {
        showAlert(PROFILE_ERROR);
        setLoading(false);
        return;
      }

      const userProfile = profileResponse.data;

      const payload = {
        token: jwsToken,
        user: userProfile.username,
        comp: userProfile.companY_NAME,
        email: userProfile.username,
        pId: userProfile.id,
        password: userProfile.password,
      };

      localStorage.setItem('jwsToken', JSON.stringify(jwsToken));
      localStorage.setItem('userProfile', JSON.stringify(payload));
      localStorage.setItem('carlineAuthPref', 'password');

      // Save credentials if remember me is checked (ONLY for regular login, not SSO)
      saveCredentials();

      // Go straight to the board — the signing-in overlay covers the transition
      navigate('/carline', { state: { uuid: userProfile.companY_NAME, user: email } });
    } catch (error) {
      console.error('Error during login:', error);

      if (error.response && error.response.status === 401) {
        showAlert('That email and password don\'t match our records. Check for typos — passwords are case-sensitive — or use "Forgot your password?" below.');
      } else if (error.response && error.response.status === 429) {
        showAlert("Let's take a short pause — there have been several sign-in tries in a row. Wait a minute or two, then try again. If you can't remember your password, use \"Forgot your password?\" below.");
      } else if (error.code === 'ECONNABORTED') {
        showAlert('This is taking longer than usual. Please check the internet connection and try again.');
      } else {
        showAlert("We can't reach ConciergePad right now. Make sure you're connected to the internet (school Wi-Fi sometimes drops), then try again.");
      }

      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 4, sm: 6 },
        overflowY: 'auto',
        // Calm static gradients — no animation, reduced-motion friendly
        background: `
          radial-gradient(1100px 700px at 85% -10%, rgba(255,255,255,0.14), transparent 60%),
          radial-gradient(900px 650px at 8% 110%, rgba(255,183,77,0.16), transparent 55%),
          linear-gradient(160deg, #3B3486 0%, #5B4B9E 55%, #7A5BA6 100%)
        `,
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 510 }}>
        <Grow in={true} timeout={700}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4.5 },
              borderRadius: { xs: '16px', sm: '20px' },
              bgcolor: '#FFFFFF',
              border: '1px solid rgba(255,255,255,0.7)',
              boxShadow: '0 24px 60px -16px rgba(28,25,70,0.45), 0 4px 12px rgba(28,25,70,0.12)',
              mx: { xs: 2, sm: 0 },
            }}
          >
            <Stack spacing={0} alignItems="stretch">
              {/* Logo + heading */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <img
                  alt="ConciergePad"
                  src={LOGO_URL}
                  style={{ width: isMobile ? 168 : 200, height: 'auto', display: 'block' }}
                />
              </Box>

              <Typography component="h1" sx={{
                fontSize: { xs: '1.35rem', sm: '1.5rem' }, fontWeight: 700,
                color: '#0F172A', letterSpacing: '-0.01em', textAlign: 'center'
              }}>
                Welcome back
              </Typography>
              <Typography sx={{ fontSize: '0.9375rem', color: '#55606E', textAlign: 'center', mt: 0.5, mb: 1.75 }}>
                Sign in to open your school's dismissal board
              </Typography>

              {/* Quiet feature strip — web icons, no emoji */}
              <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: 2.5 }}>
                {[
                  { Icon: DirectionsCarOutlinedIcon, label: 'Carline' },
                  { Icon: CameraAltOutlinedIcon, label: 'Cameras' },
                  { Icon: GroupsOutlinedIcon, label: 'Carpools' },
                ].map(({ Icon, label }) => (
                  <Stack key={label} direction="row" spacing={0.75} alignItems="center">
                    <Icon sx={{ fontSize: 18, color: '#8A93A3' }} />
                    <Typography sx={{ fontSize: '0.8125rem', color: '#8A93A3', fontWeight: 600 }}>
                      {label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              {/* Single persistent error surface — visible whichever way you sign in */}
              <Collapse in={!!errorMessage} unmountOnExit>
                <Alert
                  severity={errorSeverity}
                  role="alert"
                  onClose={() => setErrorMessage('')}
                  sx={{ borderRadius: '12px', mb: 2, fontSize: '0.9rem', alignItems: 'flex-start' }}
                >
                  {errorMessage}
                </Alert>
              </Collapse>

              {/* Google leads — it's how most staff sign in */}
              <Typography sx={{ fontSize: '0.875rem', color: '#55606E', textAlign: 'center', mb: 1 }}>
                Most staff sign in with their school Google account
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  '& > div': { width: '100%' },
                  '& .nsm7Bb-HzV7m-LgbsSe': {
                    height: '44px !important',
                    borderRadius: '12px !important',
                  },
                  '& .nsm7Bb-HzV7m-LgbsSe-BPrWId': {
                    fontSize: '15px !important',
                  }
                }}
              >
                <GoogleLogin
                  theme="outline"
                  size="large"
                  logo_alignment="center"
                  text="continue_with"
                  shape="rectangular"
                  width="100%"
                  onSuccess={credentialResponse => {
                    const idToken = credentialResponse.credential;      // the Google ID token
                    const userInfo = jwtDecode(idToken);                // local decode for picture/email display only

                    goToSso(idToken, userInfo.email, userInfo.picture);
                  }}
                  onError={() => {
                    console.log('Login Failed');
                    showAlert('Google sign-in didn\'t finish — the window may have closed early or pop-ups may be blocked. Please press "Continue with Google" to try again.');
                  }}
                />
              </Box>

              {/* Microsoft sign-in — logo drawn locally, no hotlinked images */}
              <Button
                fullWidth
                variant="outlined"
                onClick={handleMicrosoftLogin}
                disabled={loading}
                sx={{
                  mt: 1.5,
                  minHeight: 44,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: '#5e5e5e',
                  borderColor: '#D5DBE3',
                  bgcolor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.25,
                  '&:hover': { borderColor: '#8c8c8c', bgcolor: '#f8f8f8' },
                }}
              >
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: '9px 9px',
                  gridTemplateRows: '9px 9px',
                  gap: '2px'
                }} aria-hidden>
                  <Box sx={{ backgroundColor: '#f25022' }} />
                  <Box sx={{ backgroundColor: '#7fba00' }} />
                  <Box sx={{ backgroundColor: '#00a4ef' }} />
                  <Box sx={{ backgroundColor: '#ffb900' }} />
                </Box>
                Sign in with Microsoft
              </Button>

              {/* Divider */}
              <Box sx={{ my: 2.5 }}>
                <Divider>
                  <Typography sx={{ px: 1.5, fontSize: '0.8125rem', color: '#8A93A3' }}>
                    or
                  </Typography>
                </Divider>
              </Box>

              {/* Email/password — collapsed for first-time visitors, open for
                  returning password users (saved credentials or prior use) */}
              {!showEmailForm && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setShowEmailForm(true);
                    setTimeout(() => emailRef.current?.focus(), 350);
                  }}
                  sx={{
                    minHeight: 48,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    px: 2,
                    color: ACCENT,
                    borderColor: '#D5DBE3',
                    '&:hover': { borderColor: ACCENT, bgcolor: 'rgba(90,79,207,0.04)' },
                  }}
                >
                  Sign in with email and password instead
                </Button>
              )}

              <Collapse in={showEmailForm} unmountOnExit>
                <Box component="form" onSubmit={handleLogin} noValidate sx={{ width: '100%' }}>
                  <Stack spacing={2.25}>
                    <TextField
                      fullWidth
                      label="Email address"
                      type="email"
                      name="email"
                      autoComplete="username"
                      inputMode="email"
                      inputRef={emailRef}
                      value={email}
                      onChange={handleEmailChange}
                      onBlur={handleEmailBlur}
                      error={!!emailError}
                      helperText={emailError}
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />

                    <TextField
                      fullWidth
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete="current-password"
                      inputRef={passwordRef}
                      value={password}
                      onChange={handlePasswordChange}
                      onKeyDown={handleCapsLockCheck}
                      onKeyUp={handleCapsLockCheck}
                      error={!!passwordError}
                      helperText={passwordError || (capsLockOn ? 'Caps Lock is on' : '')}
                      FormHelperTextProps={
                        !passwordError && capsLockOn
                          ? { sx: { color: '#B45309', fontWeight: 600 } }
                          : undefined
                      }
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={loading}
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />

                    {/* Remember me + forgot password */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: -0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={rememberMe}
                            onChange={handleRememberMeChange}
                            disabled={loading}
                            sx={{ color: '#8A93A3', '&.Mui-checked': { color: ACCENT } }}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: '0.875rem', color: '#55606E', userSelect: 'none' }}>
                            Remember me on this device
                          </Typography>
                        }
                        sx={{ ml: 0, mr: 0 }}
                      />
                      <Link
                        href="https://www.conciergepadedu.com/lost_password.aspx"
                        target="_blank"
                        rel="noopener"
                        underline="always"
                        sx={{ fontSize: '0.875rem', fontWeight: 600, color: ACCENT, whiteSpace: 'nowrap' }}
                      >
                        Forgot your password?
                      </Link>
                    </Stack>

                    {/* Sign-in button — only disabled while actually signing in */}
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{
                        minHeight: { xs: 52, sm: 50 },
                        py: 1.5,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        bgcolor: ACCENT,
                        boxShadow: '0 2px 8px rgba(90,79,207,0.35)',
                        '&:hover': { bgcolor: ACCENT_HOVER, boxShadow: '0 4px 14px rgba(90,79,207,0.40)' },
                        '&:active': { bgcolor: ACCENT_ACTIVE },
                        '&:disabled': { bgcolor: '#C9CDD6', color: '#FFFFFF' },
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign in'}
                    </Button>
                  </Stack>
                </Box>
              </Collapse>

              {/* Footer */}
              <Typography sx={{ fontSize: '0.8125rem', color: '#8A93A3', textAlign: 'center', mt: 2.5 }}>
                Need access? Ask your school's ConciergePad admin.
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#A5ACB8', textAlign: 'center', mt: 0.75 }}>
                By signing in, you agree to ConciergePad's Terms of Service.
              </Typography>
            </Stack>
          </Paper>
        </Grow>
      </Container>

      {/* Full-screen cover while authenticating and handing off to the board —
          appears the instant the Google popup closes, so there's no dead gap */}
      <Fade in={loading} unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            bgcolor: 'rgba(255, 255, 255, 0.96)',
            backdropFilter: 'blur(6px)'
          }}
        >
          <img src={LOGO_URL} alt="" style={{ width: 160, height: 'auto' }} />
          <CircularProgress size={44} sx={{ color: ACCENT }} />
          <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '1.05rem' }}>
            Signing you in…
          </Typography>
          <Typography sx={{ color: '#64748B', fontSize: '0.875rem', px: 3, textAlign: 'center' }}>
            {slowNetwork
              ? 'Still working — the connection is a little slow. Hang tight.'
              : 'Getting your dismissal board ready — this takes just a moment.'}
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default Login;
